import React from 'react';
import {useQuery} from '@tanstack/react-query';
import {useNavigate} from 'react-router';

import {useExperimentsStore, useTemporalStore} from 'apps/experiments/src/store';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {getReportUrl} from 'common/report/util';
import {experimentQueryKeyForEntity, featureFlagsQueryKeyForEntity} from 'apps/experiments/src/react-query';
import {useExperimentsApi} from 'apps/experiments/src/api/experiments';
import {useFeatureFlagsApi} from 'apps/feature-flags/src/api/feature-flags';
import {ExperimentCollectionType} from 'iron/apps/experiments/src/types';

import {PageTrackingEvent, trackExperimentPageAction} from '../tracking';
import {DEFAULT_EXPERIMENT, NEW_EXPERIMENT_ID} from '../utils';

type FetchExperimentParams = {
  projectId: number;
  workspaceId: number;
  experimentId: string;
  featureFlagIntegrationEnabled: boolean;
};

export function useQueryExperiment({
  projectId,
  workspaceId,
  experimentId,
  featureFlagIntegrationEnabled,
}: FetchExperimentParams) {
  const experiment = useExperimentsStore((state) => state.experiment);
  const setExperiment = useExperimentsStore((state) => state.setExperiment);
  const setExperimentLoading = useExperimentsStore((state) => state.setExperimentLoading);
  const setCurrentFeatureFlag = useExperimentsStore((state) => state.setCurrentFeatureFlag);
  const {pause: pauseHistory, resume: resumeHistory, clear: clearHistory} = useTemporalStore((state) => state);
  const reset = useExperimentsStore((state) => state.reset);
  const navigate = useNavigate();

  const {getExperiment} = useExperimentsApi({
    projectId,
    workspaceId,
  });
  const {getFeatureFlag} = useFeatureFlagsApi({
    projectId,
  });

  const {data, isLoading, isError, error, isSuccess} = useQuery({
    queryKey: experimentQueryKeyForEntity(projectId, workspaceId, experimentId),
    queryFn: async () => {
      reset();
      clearHistory();
      return getExperiment({id: experimentId});
    },
    enabled: !!experimentId && experimentId !== NEW_EXPERIMENT_ID,
  });

  const featureFlagId = experiment?.settings?.featureFlagSettings?.featureFlagId ?? ``;
  const {data: featureFlag} = useQuery({
    queryKey: featureFlagsQueryKeyForEntity(projectId, featureFlagId),
    queryFn: async () => {
      return getFeatureFlag({id: featureFlagId});
    },
    enabled: !!featureFlagId,
  });

  React.useEffect(() => {
    if (isError) {
      addToast({
        message: error.message,
        type: ToastType.ERROR,
      });
      navigate(
        getReportUrl(`experiments`, {
          projectId,
          workspaceId,
        }),
      );
    }
  }, [isError, error]);

  React.useEffect(() => {
    clearHistory();
    if (experimentId === NEW_EXPERIMENT_ID) {
      const newExperiment = {
        ...DEFAULT_EXPERIMENT,
      };
      if (featureFlagIntegrationEnabled) {
        newExperiment.settings.collectionMethod = ExperimentCollectionType.FEATURE_FLAG;
      }
      setExperiment(newExperiment);
      return;
    }
    if (isSuccess && data) {
      pauseHistory();
      setExperiment(data);
      resumeHistory();
      trackExperimentPageAction({
        event: PageTrackingEvent.PageLoaded,
        experiment: data,
      });
    }
  }, [isSuccess, data, experimentId, featureFlagIntegrationEnabled]);

  React.useEffect(() => {
    if (!featureFlag || !featureFlagId) {
      setCurrentFeatureFlag(undefined);
    } else if (featureFlag) {
      setCurrentFeatureFlag(featureFlag);
    }
  }, [featureFlag, featureFlagId]);

  React.useEffect(() => {
    setExperimentLoading(isLoading);
  }, [isLoading]);
}
